import React, { useState, useEffect } from 'react';
import { PageHeader } from '../molecules/PageHeader';
import { MiningGroupsContainer } from '../organisms/MiningGroupsContainer';
import { loadSettingsFromStorage, loadPayoutsFromStorage, getHiddenTokenIds, setHiddenTokenIds } from '../../utils/storage';
import { formatCurrency } from '../../utils/date';
import {
  aggregateTokens,
  updateTokenPrices,
  priceToken,
  getLastKnownPrice,
  calculateTotalElectricityCostForGroups,
  calculateMinimumSalePriceForMining,
  calculateMinimumSalePriceForMixed,
  type TokenSummary
} from '../../utils/tokens';
import {
  loadMiningConfigs,
  addMiningGroup,
  updateMiningGroup,
  removeMiningGroup
} from '../../utils/miningStorage';
import type { GlobalSettings } from '../../types/settings';
import type { Payout } from '../../types/payout';
import type { MiningConfigs, MiningGroup } from '../../types/mining';

const CryptoCard: React.FC<{
  token: TokenSummary;
  electricityCost: number;
  settings: GlobalSettings | null;
  onHide: (tokenId: string) => void;
  isRefreshingPrice?: boolean;
}> = ({ token, electricityCost, settings, onHide, isRefreshingPrice = false }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [manualPrice, setManualPrice] = useState<number | null>(null);
  const [customSalePrice, setCustomSalePrice] = useState<number | null>(null);

  const electricityCostPerToken = token.totalAmount > 0 ? electricityCost / token.totalAmount : 0;
  const effectiveCurrentPrice = token.currentPrice > 0 ? token.currentPrice : (manualPrice || 0);
  const hasAchatTransactions = token.payouts.some(p => p.type === 'achat');
  const hasSales = token.totalSaleProceeds > 0;
  const isFullySold = token.totalAmount <= 0 && hasSales;
  const bncRate = settings?.bncRate || 0.34;
  const minimumSalePrice = hasAchatTransactions
    ? calculateMinimumSalePriceForMixed(token.payouts, electricityCost, bncRate)
    : calculateMinimumSalePriceForMining(token.averagePurchasePrice, electricityCostPerToken, bncRate);

  const effectiveCurrentTotalValue = token.totalAmount * effectiveCurrentPrice;
  // Unrealized P&L on remaining coins
  const unrealizedProfitLoss = effectiveCurrentTotalValue - token.totalValueAtPurchase;
  // Realized P&L from sales
  const realizedProfitLoss = token.totalSaleProceeds - token.totalRealizedCostBasis;
  // Total P&L = realized + unrealized
  const totalProfitLoss = realizedProfitLoss + unrealizedProfitLoss;
  const totalCostBasis = token.totalValueAtPurchase + token.totalRealizedCostBasis;
  const totalProfitLossPercentage = totalCostBasis > 0 ? (totalProfitLoss / totalCostBasis) * 100 : 0;

  // For the header gradient, use total P&L when there are sales, otherwise unrealized
  const effectiveProfitLoss = hasSales ? totalProfitLoss : unrealizedProfitLoss;

  const bncAmount = typeof token.totalBNCAmount === 'number' && !isNaN(token.totalBNCAmount)
    ? token.totalBNCAmount
    : (token.totalValueAtPurchase * bncRate);
  const bncAmountPerToken = token.totalAmount > 0 ? bncAmount / token.totalAmount : 0;

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
      <div className={`px-6 py-4 border-b ${
        effectiveProfitLoss >= 0
          ? 'bg-gradient-to-r from-green-50 to-emerald-100'
          : 'bg-gradient-to-r from-red-50 to-pink-100'
      }`}>
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100">{token.crypto.name}</h3>
            <p className="text-sm text-blue-600 font-semibold uppercase tracking-wide">{token.crypto.symbol}</p>
            {isFullySold && (
              <span className="inline-block mt-1 px-2 py-0.5 text-xs font-semibold bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-400 rounded">
                Position fermée
              </span>
            )}
          </div>
          <div className="text-right flex flex-col items-end">
            <button
              type="button"
              onClick={() => onHide(token.crypto.id)}
              title="Masquer ce token"
              aria-label="Masquer ce token"
              className="mb-2 p-1.5 rounded-lg text-gray-500 dark:text-gray-400 hover:text-gray-700 hover:dark:text-gray-300 hover:bg-white/60 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.451 10.451 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88" />
              </svg>
            </button>
            <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">{token.totalAmount.toFixed(8)}</div>
            <div className="text-sm text-gray-500 dark:text-gray-400">tokens</div>
          </div>
        </div>
      </div>

      <div className="px-6 py-4">
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="w-full flex items-center justify-between py-2 text-left focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-inset rounded-lg transition-colors"
        >
          <span className="text-lg font-semibold text-gray-900 dark:text-gray-100">Détails financiers</span>
          <svg
            className={`w-5 h-5 text-gray-400 dark:text-gray-500 transform transition-transform duration-200 ${
              isExpanded ? 'rotate-180' : ''
            }`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>

        <div className={`transition-all duration-300 ease-in-out ${
          isExpanded ? 'max-h-none opacity-100 mt-4' : 'max-h-0 opacity-0 overflow-hidden'
        }`}>
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-purple-50 rounded-lg p-4">
                <div className="text-sm font-medium text-purple-600 mb-1">Prix moyen d&apos;acquisition HT</div>
                <div className="text-xl font-bold text-purple-900">{formatCurrency(token.averagePurchasePrice)}</div>
                <div className="text-xs text-purple-600 mt-1">
                  {electricityCostPerToken > 0
                    ? `+ ${formatCurrency(electricityCostPerToken)} électricité (minage uniquement)`
                    : hasAchatTransactions && !token.payouts.some(p => p.type === 'payout' || !p.type)
                      ? 'Pas de tokens minés'
                      : 'Aucun coût électrique configuré'
                  }
                </div>
              </div>

              <div className="bg-blue-50 rounded-lg p-4">
                <div className="text-sm font-medium text-blue-600 mb-1 flex items-center gap-2">
                  Prix actuel
                  {isRefreshingPrice && (
                    <span
                      className="animate-spin h-3 w-3 border-2 border-blue-400 border-t-transparent rounded-full"
                      title="Actualisation du prix en cours"
                    />
                  )}
                </div>
                <div className="text-xl font-bold text-blue-900">{formatCurrency(effectiveCurrentPrice)}</div>
                {isRefreshingPrice && token.currentPrice === 0 && (
                  <div className="text-xs text-blue-500 mt-1">Récupération du prix en cours...</div>
                )}
                {!isRefreshingPrice && token.currentPrice === 0 && (
                  <div className="mt-2">
                    <input
                      type="number"
                      step="0.01"
                      placeholder="Saisir prix manuel"
                      value={manualPrice || ''}
                      onChange={(e) => {
                        const value = parseFloat(e.target.value);
                        setManualPrice(isNaN(value) ? null : value);
                      }}
                      className="w-full px-2 py-1 text-sm border border-blue-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                    />
                    <div className="text-xs text-blue-600 mt-1">Prix API indisponible - saisie manuelle</div>
                  </div>
                )}
              </div>

              <div className="bg-green-50 rounded-lg p-4">
                <div className="text-sm font-medium text-green-600 mb-1">Valeur totale portfolio</div>
                <div className="text-xl font-bold text-green-900">{formatCurrency(effectiveCurrentTotalValue)}</div>
              </div>
            </div>

            <div className="space-y-3">
              {hasSales && (
                <div className={`rounded-lg p-4 ${
                  realizedProfitLoss >= 0 ? 'bg-emerald-50 border-emerald-200' : 'bg-red-50 border-red-200'
                } border`}>
                  <div className={`text-sm font-medium mb-1 ${
                    realizedProfitLoss >= 0 ? 'text-emerald-600' : 'text-red-600'
                  }`}>
                    P&L réalisé (ventes)
                  </div>
                  <div className={`text-xl font-bold ${
                    realizedProfitLoss >= 0 ? 'text-emerald-900' : 'text-red-900'
                  }`}>
                    {realizedProfitLoss >= 0 ? '+' : ''}{formatCurrency(realizedProfitLoss)}
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    Produit de cession : {formatCurrency(token.totalSaleProceeds)} — Base de coût : {formatCurrency(token.totalRealizedCostBasis)}
                  </div>
                </div>
              )}

              {!isFullySold && (
                <div className={`rounded-lg p-4 ${
                  unrealizedProfitLoss >= 0 ? 'bg-emerald-50 border-emerald-200' : 'bg-red-50 border-red-200'
                } border`}>
                  <div className={`text-sm font-medium mb-1 ${
                    unrealizedProfitLoss >= 0 ? 'text-emerald-600' : 'text-red-600'
                  }`}>
                    {hasSales ? 'P&L non réalisé (coins restants)' : 'Plus/Moins value (brute) — payout vs actuel'}
                  </div>
                  <div className={`text-xl font-bold ${
                    unrealizedProfitLoss >= 0 ? 'text-emerald-900' : 'text-red-900'
                  }`}>
                    {unrealizedProfitLoss >= 0 ? '+' : ''}{formatCurrency(unrealizedProfitLoss)}
                  </div>
                </div>
              )}

              {hasSales && (
                <div className={`rounded-lg p-4 border-2 ${
                  totalProfitLoss >= 0 ? 'bg-emerald-100 border-emerald-400' : 'bg-red-100 border-red-400'
                }`}>
                  <div className={`text-sm font-medium mb-1 ${
                    totalProfitLoss >= 0 ? 'text-emerald-700' : 'text-red-700'
                  }`}>
                    P&L total (réalisé + non réalisé)
                  </div>
                  <div className={`text-xl font-bold ${
                    totalProfitLoss >= 0 ? 'text-emerald-900' : 'text-red-900'
                  }`}>
                    {totalProfitLoss >= 0 ? '+' : ''}{formatCurrency(totalProfitLoss)}
                  </div>
                  <div className={`text-sm ${
                    totalProfitLoss >= 0 ? 'text-emerald-600' : 'text-red-600'
                  }`}>
                    ({totalProfitLossPercentage >= 0 ? '+' : ''}{totalProfitLossPercentage.toFixed(2)}%)
                  </div>
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-orange-50 rounded-lg p-4 border border-orange-200">
                <div className="text-sm font-medium text-orange-600 mb-1">Coût électrique global estimé</div>
                <div className="text-lg font-bold text-orange-900">{formatCurrency(electricityCost)}</div>
                <div className="text-xs text-orange-600 mt-1">{formatCurrency(electricityCostPerToken)}/token</div>
              </div>

              <div className="bg-red-50 rounded-lg p-4 border border-red-200">
                <div className="text-sm font-medium text-red-600 mb-1">Montant BNC ({Math.round(bncRate * 100)}% sur revenus)</div>
                <div className="text-lg font-bold text-red-900">{formatCurrency(bncAmount)}</div>
                <div className="text-xs text-red-600 mt-1">{formatCurrency(bncAmountPerToken)}/token</div>
              </div>
            </div>

            {!isFullySold && <div className="space-y-3 pt-4 border-t border-gray-200 dark:border-gray-700">
              <h4 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3">Prix de vente minimal pour être rentable</h4>

              <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                <div className="text-sm font-medium text-blue-600 mb-1">Prix minimal par token</div>
                <div className="text-xl font-bold text-blue-900">{formatCurrency(minimumSalePrice)}</div>
                <div className="text-xs text-blue-600 mt-1">Coût électrique + BNC + 5% marge</div>
                <div className="text-sm text-blue-700 mt-2 font-medium">
                  Total: {formatCurrency(token.totalAmount * minimumSalePrice)}
                </div>
              </div>

              <div className="mt-6 bg-gradient-to-br from-indigo-50 to-purple-50 rounded-lg p-5 border-2 border-indigo-200">
                <h4 className="text-base font-semibold text-gray-900 dark:text-gray-100 mb-4">Calculer la marge pour un prix de vente</h4>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Prix de vente par token ({token.crypto.symbol})
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      placeholder="Saisir le prix de vente"
                      value={customSalePrice ?? ''}
                      onChange={(e) => {
                        const value = parseFloat(e.target.value);
                        setCustomSalePrice(isNaN(value) ? null : value);
                      }}
                      className="w-full px-3 py-2 border border-indigo-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>

                  {customSalePrice !== null && customSalePrice > 0 && (
                    <div className="space-y-3 mt-4">
                      <div className="text-xs text-gray-600 dark:text-gray-400 bg-white dark:bg-gray-800 rounded-lg p-3 space-y-2">
                        <div className="flex justify-between">
                          <span>Coût par token (achat):</span>
                          <span className="font-medium">{formatCurrency(token.averagePurchasePrice)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Coût électrique par token:</span>
                          <span className="font-medium">{formatCurrency(electricityCostPerToken)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span>BNC par token ({Math.round(bncRate * 100)}%):</span>
                          <span className="font-medium">{formatCurrency(bncAmountPerToken)}</span>
                        </div>
                        <div className="border-t border-gray-200 dark:border-gray-700 pt-2 flex justify-between font-semibold">
                          <span>Coût total par token:</span>
                          <span>{formatCurrency(token.averagePurchasePrice + electricityCostPerToken + bncAmountPerToken)}</span>
                        </div>
                      </div>

                      {(() => {
                        const totalCostPerToken = token.averagePurchasePrice + electricityCostPerToken + bncAmountPerToken;
                        const netMarginPerToken = customSalePrice - totalCostPerToken;
                        const netMarginTotal = netMarginPerToken * token.totalAmount;
                        const marginPercentage = totalCostPerToken > 0 ? (netMarginPerToken / totalCostPerToken) * 100 : 0;
                        const isPositive = netMarginPerToken >= 0;

                        return (
                          <div className={`rounded-lg p-4 border-2 ${
                            isPositive
                              ? 'bg-emerald-50 border-emerald-300'
                              : 'bg-red-50 border-red-300'
                          }`}>
                            <div className="space-y-3">
                              <div>
                                <div className={`text-sm font-medium mb-1 ${
                                  isPositive ? 'text-emerald-600' : 'text-red-600'
                                }`}>
                                  Marge nette par token
                                </div>
                                <div className={`text-2xl font-bold ${
                                  isPositive ? 'text-emerald-900' : 'text-red-900'
                                }`}>
                                  {isPositive ? '+' : ''}{formatCurrency(netMarginPerToken)}
                                </div>
                              </div>
                              <div className="border-t border-gray-300 dark:border-gray-600 pt-3">
                                <div className={`text-sm font-medium mb-1 ${
                                  isPositive ? 'text-emerald-600' : 'text-red-600'
                                }`}>
                                  Marge nette totale (×{token.totalAmount.toFixed(8)} tokens)
                                </div>
                                <div className={`text-xl font-bold ${
                                  isPositive ? 'text-emerald-900' : 'text-red-900'
                                }`}>
                                  {isPositive ? '+' : ''}{formatCurrency(netMarginTotal)}
                                </div>
                              </div>
                              <div className="flex items-center gap-2 text-sm font-medium">
                                <div className={`px-2 py-1 rounded ${
                                  isPositive
                                    ? 'bg-emerald-200 text-emerald-800'
                                    : 'bg-red-200 text-red-800'
                                }`}>
                                  {isPositive ? '+' : ''}{marginPercentage.toFixed(1)}%
                                </div>
                                <span className="text-gray-600 dark:text-gray-400">
                                  {isPositive ? 'de marge' : 'de perte'}
                                </span>
                              </div>
                            </div>
                          </div>
                        );
                      })()}
                    </div>
                  )}
                </div>
              </div>
            </div>}
          </div>
        </div>
      </div>
    </div>
  );
};

export const PrixVentePage: React.FC = () => {
  const [settings, setSettings] = useState<GlobalSettings | null>(null);
  const [payouts, setPayouts] = useState<Payout[]>([]);
  const [tokens, setTokens] = useState<TokenSummary[]>([]);
  const [tokenElectricityCosts, setTokenElectricityCosts] = useState<Record<string, number>>({});
  const [miningConfigs, setMiningConfigs] = useState<MiningConfigs>({});
  const [hiddenTokenIds, setHiddenTokenIdsState] = useState<string[]>([]);
  const [refreshingTokenIds, setRefreshingTokenIds] = useState<Set<string>>(new Set());
  const [activeView, setActiveView] = useState<'portfolio' | 'mining'>('portfolio');

  useEffect(() => {
    const loadData = async () => {
      try {
        const storedSettings = await loadSettingsFromStorage();
        const storedPayouts = await loadPayoutsFromStorage();
        setSettings(storedSettings);
        setPayouts(storedPayouts);
      } catch (error) {
        console.error('Erreur lors du chargement des données:', error);
      }
    };

    loadData();
    setHiddenTokenIdsState(getHiddenTokenIds());
  }, []);

  const handleHideToken = (tokenId: string) => {
    setHiddenTokenIdsState(prev => {
      if (prev.includes(tokenId)) return prev;
      const updated = [...prev, tokenId];
      setHiddenTokenIds(updated);
      return updated;
    });
  };

  const handleUnhideToken = (tokenId: string) => {
    setHiddenTokenIdsState(prev => {
      const updated = prev.filter(id => id !== tokenId);
      setHiddenTokenIds(updated);
      return updated;
    });
  };

  useEffect(() => {
    const loadTokens = async () => {
      if (payouts.length === 0) return;

      const bncRate = settings?.bncRate || 0.34;
      const aggregatedTokens = aggregateTokens(payouts, bncRate);
      const tokenArray = Array.from(aggregatedTokens.values());

      // Pré-rendu immédiat avec le dernier prix connu (cache localStorage) au lieu d'attendre
      // que la file CoinGecko résolve chaque token — le portefeuille s'affiche tout de suite,
      // les prix se rafraîchissent ensuite au fur et à mesure (voir onTokenPriced ci-dessous).
      const seededTokens = tokenArray.map((token) => {
        const lastKnownPrice = getLastKnownPrice(token.crypto.id);
        return lastKnownPrice !== undefined ? priceToken(token, lastKnownPrice) : token;
      });
      setTokens(seededTokens);
      setRefreshingTokenIds(new Set(tokenArray.map((token) => token.crypto.id)));

      try {
        await updateTokenPrices(tokenArray, (pricedToken) => {
          setTokens((prev) => prev.map((t) => (t.crypto.id === pricedToken.crypto.id ? pricedToken : t)));
          setRefreshingTokenIds((prev) => {
            if (!prev.has(pricedToken.crypto.id)) return prev;
            const next = new Set(prev);
            next.delete(pricedToken.crypto.id);
            return next;
          });
        });
      } catch (error) {
        console.error('Erreur lors du chargement des tokens:', error);
      }
    };

    loadTokens();
  }, [payouts, settings]);

  const loadElectricityCosts = async () => {
    if (!settings || tokens.length === 0) return;

    const electricityCosts: Record<string, number> = {};
    const configs = await loadMiningConfigs();

    const hcTariff = settings.electricityCosts.tariffType === 'hc_hp'
      ? (settings.electricityCosts.hcTariff || 0)
      : (settings.electricityCosts.simpleTariff || 0);
    const hpTariff = settings.electricityCosts.tariffType === 'hc_hp'
      ? (settings.electricityCosts.hpTariff || 0)
      : (settings.electricityCosts.simpleTariff || 0);

    const solarEnabled = settings.solarPanels.enabled;
    const solarProduction = settings.solarPanels.averageDailyProduction || 0;

    tokens.forEach(token => {
      const groups = configs[token.crypto.id] || [];
      const totalElectricityCost = calculateTotalElectricityCostForGroups(
        groups,
        hcTariff,
        hpTariff,
        solarEnabled,
        solarProduction
      );
      electricityCosts[token.crypto.id] = totalElectricityCost;
    });

    setTokenElectricityCosts(electricityCosts);
    setMiningConfigs(configs);
  };

  useEffect(() => {
    if (tokens.length > 0) {
      loadElectricityCosts();
    }
  }, [tokens, settings]);

  const visibleTokens = tokens.filter(token => !hiddenTokenIds.includes(token.crypto.id));
  const hiddenTokens = tokens.filter(token => hiddenTokenIds.includes(token.crypto.id));

  const handleAddMiningGroup = async (tokenId: string, group: Omit<MiningGroup, 'id'>) => {
    await addMiningGroup(tokenId, group);
    loadElectricityCosts();
  };

  const handleUpdateMiningGroup = async (tokenId: string, groupId: string, updatedGroup: Partial<Omit<MiningGroup, 'id'>>) => {
    await updateMiningGroup(tokenId, groupId, updatedGroup);
    loadElectricityCosts();
  };

  const handleRemoveMiningGroup = async (tokenId: string, groupId: string) => {
    await removeMiningGroup(tokenId, groupId);
    loadElectricityCosts();
  };

  return (
    <div className="container mx-auto px-4 pt-8 pb-8">
      <div className="max-w-6xl mx-auto">
        <PageHeader
          title="Mes tokens"
          subtitle="Analyse détaillée de votre portfolio avec calculs fiscaux et prix de vente optimaux"
        />

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 overflow-hidden mb-6">
          <div className="flex border-b border-gray-200 dark:border-gray-700">
            <button
              onClick={() => setActiveView('portfolio')}
              className={`flex-1 px-6 py-3 text-sm font-medium transition-colors ${
                activeView === 'portfolio'
                  ? 'bg-blue-50 text-blue-700 border-b-2 border-blue-500'
                  : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 hover:dark:text-gray-300 hover:bg-gray-50 hover:dark:bg-gray-900'
              }`}
            >
              <div className="flex items-center justify-center">
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
                Portfolio & Prix de vente
              </div>
            </button>
            <button
              onClick={() => setActiveView('mining')}
              className={`flex-1 px-6 py-3 text-sm font-medium transition-colors ${
                activeView === 'mining'
                  ? 'bg-yellow-50 text-yellow-700 border-b-2 border-yellow-500'
                  : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 hover:dark:text-gray-300 hover:bg-gray-50 hover:dark:bg-gray-900'
              }`}
            >
              <div className="flex items-center justify-center">
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
                Groupes de minage
              </div>
            </button>
          </div>
        </div>

        {activeView === 'portfolio' && (
          <div className="space-y-6">
            {tokens.length === 0 ? (
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 p-8 text-center">
                <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
                  Aucun token dans le portefeuille
                </h3>
                <p className="text-gray-500 dark:text-gray-400">
                  Ajoutez des payouts dans la section &quot;Payouts&quot; pour voir l&apos;analyse
                </p>
              </div>
            ) : (
              <>
                {visibleTokens.length === 0 ? (
                  <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 p-8 text-center">
                    <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
                      Tous les tokens sont masqués
                    </h3>
                    <p className="text-gray-500 dark:text-gray-400">
                      Démasquez un token ci-dessous pour l&apos;afficher
                    </p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
                    {visibleTokens.map((token) => (
                      <CryptoCard
                        key={token.crypto.id}
                        token={token}
                        electricityCost={tokenElectricityCosts[token.crypto.id] || 0}
                        settings={settings}
                        onHide={handleHideToken}
                        isRefreshingPrice={refreshingTokenIds.has(token.crypto.id)}
                      />
                    ))}
                  </div>
                )}

                {hiddenTokens.length > 0 && (
                  <div className="bg-white dark:bg-gray-800 rounded-xl shadow border border-gray-200 dark:border-gray-700 p-4">
                    <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
                      Tokens masqués ({hiddenTokens.length})
                    </h3>
                    <div className="flex flex-wrap gap-2">
                      {hiddenTokens.map((token) => (
                        <button
                          key={token.crypto.id}
                          type="button"
                          onClick={() => handleUnhideToken(token.crypto.id)}
                          title="Démasquer ce token"
                          className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 hover:bg-gray-100 hover:dark:bg-gray-700 text-sm text-gray-700 dark:text-gray-300 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          <span className="font-medium">{token.crypto.symbol}</span>
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          </svg>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {activeView === 'mining' && (
          <MiningGroupsContainer
            tokens={tokens}
            miningConfigs={miningConfigs}
            onAddMiningGroup={handleAddMiningGroup}
            onUpdateMiningGroup={handleUpdateMiningGroup}
            onRemoveMiningGroup={handleRemoveMiningGroup}
          />
        )}
      </div>
    </div>
  );
};
